import { useEffect, useState } from "react";
import { portalFetch } from "../api/client";
import { usePagedPortalItems } from "../hooks/usePagedPortalItems";
import type { PortalOrganizationSummaryDto, PortalProjectDto, PortalProposalDto } from "../model";
import { PortalSelect } from "./PortalSelect";
import { StatusBanner } from "./StatusBanner";

type ProjectGroup = {
	key: string;
	name: string;
	organizationId?: string;
	projects: PortalProjectDto[];
};

export function defaultOwningOrganizationId(organizations: PortalOrganizationSummaryDto[], current = ""): string {
	if (organizations.some((organization) => organization.id === current)) return current;
	return organizations.length === 1 ? (organizations[0]?.id ?? "") : "";
}

export function groupProjectsByOwningContext(projects: PortalProjectDto[]): ProjectGroup[] {
	const directProjects = projects.filter((project) => project.owningContext?.type !== "organization");
	const organizations = new Map<string, ProjectGroup>();
	for (const project of projects) {
		if (project.owningContext?.type !== "organization") continue;
		const { organization } = project.owningContext;
		const group = organizations.get(organization.id) ?? {
			key: `organization:${organization.id}`,
			name: organization.name,
			organizationId: organization.id,
			projects: [],
		};
		group.projects.push(project);
		organizations.set(organization.id, group);
	}
	return [
		...(directProjects.length ? [{ key: "direct", name: "Direct work", projects: directProjects }] : []),
		...[...organizations.values()].sort((left, right) => left.name.localeCompare(right.name)),
	];
}

function projectSummary(project: PortalProjectDto): string {
	const description = project.description?.trim();
	if (description) return description;
	if (project.isComplete) return "All approved acceptance criteria are met and verified.";
	if (project.status === "planning") return "Shape and approve the project spec.";
	if (project.status === "paused") return "Paused until the work is ready to resume.";
	if (project.status === "archived") return "Archived project record.";
	return "Continue the spec, conversation, or delivery.";
}

export function ProjectListView({
	onSelectProject,
	onSelectProposal,
	onClaimInvitation,
}: {
	onSelectProject: (projectId: string) => void;
	onSelectProposal: (proposalId: string) => void;
	onClaimInvitation: () => void;
}) {
	const projectPage = usePagedPortalItems<PortalProjectDto>("/api/portal/v1/projects");
	const proposalPage = usePagedPortalItems<PortalProposalDto>("/api/portal/v1/proposals");
	const projects = projectPage.items;
	const proposals = proposalPage.items;
	const [creating, setCreating] = useState(false);
	const [newProposalOpen, setNewProposalOpen] = useState(false);
	const [newTitle, setNewTitle] = useState("");
	const [newBrief, setNewBrief] = useState("");
	const [eligibleOrganizations, setEligibleOrganizations] = useState<PortalOrganizationSummaryDto[]>([]);
	const [organizationsLoading, setOrganizationsLoading] = useState(true);
	const [organizationsFailed, setOrganizationsFailed] = useState(false);
	const [owningOrganizationId, setOwningOrganizationId] = useState("");
	const [actionError, setActionError] = useState<string | null>(null);
	const [projectFilter, setProjectFilter] = useState("all");
	const [accessInfoProjectId, setAccessInfoProjectId] = useState<string | null>(null);
	useEffect(() => {
		let active = true;
		portalFetch<PortalOrganizationSummaryDto[]>("/api/portal/v1/organizations")
			.then((organizations) => {
				if (!active) return;
				setEligibleOrganizations(organizations);
				setOwningOrganizationId((current) => defaultOwningOrganizationId(organizations, current));
			})
			.catch(() => active && setOrganizationsFailed(true))
			.finally(() => active && setOrganizationsLoading(false));
		return () => {
			active = false;
		};
	}, []);
	if (projectPage.failed || proposalPage.failed)
		return <StatusBanner kind="error">Your project workspace could not be loaded.</StatusBanner>;
	if (projectPage.loading || proposalPage.loading)
		return <StatusBanner kind="info">Loading your shared workspaces…</StatusBanner>;
	const actionableProposals = proposals.filter(
		(proposal) => proposal.status !== "accepted" && proposal.status !== "withdrawn",
	);
	const projectGroups = groupProjectsByOwningContext(projects);
	const organizationGroups = projectGroups.filter((group) => group.organizationId);
	const visibleProjectGroups = projectGroups.filter((group) => projectFilter === "all" || group.key === projectFilter);
	const selectedOwningOrganization = eligibleOrganizations.find(
		(organization) => organization.id === owningOrganizationId,
	);
	const soleEligibleOrganization = eligibleOrganizations.length === 1 ? eligibleOrganizations[0] : undefined;
	const ownershipReady =
		!organizationsLoading &&
		!organizationsFailed &&
		(eligibleOrganizations.length <= 1 || Boolean(selectedOwningOrganization));
	async function createProposal() {
		if (!newTitle.trim() || !newBrief.trim() || !ownershipReady) return;
		setCreating(true);
		setActionError(null);
		try {
			const proposal = await portalFetch<PortalProposalDto>("/api/portal/v1/proposals", {
				method: "POST",
				body: JSON.stringify({
					title: newTitle.trim(),
					draftContent: newBrief.trim(),
					...(selectedOwningOrganization ? { owningOrganizationId: selectedOwningOrganization.id } : {}),
				}),
			});
			proposalPage.setItems((current) => [proposal, ...current.filter((item) => item.id !== proposal.id)]);
			onSelectProposal(proposal.id);
		} catch {
			setActionError("The proposal could not be created. Please try again.");
		} finally {
			setCreating(false);
		}
	}
	async function dismissRequirementsUpdate(proposal: PortalProposalDto) {
		if (!window.confirm(`Dismiss “${proposal.title}”? It will be removed from your active proposals.`)) return;
		setActionError(null);
		try {
			await portalFetch<PortalProposalDto>(`/api/portal/v1/proposals/${proposal.id}/dismiss`, {
				method: "POST",
				body: JSON.stringify({}),
			});
			proposalPage.setItems((current) => current.filter((item) => item.id !== proposal.id));
		} catch {
			setActionError("The requirements update could not be dismissed. Please try again.");
		}
	}
	return (
		<div className="workspace-index">
			<section className="new-proposal-panel" aria-labelledby="new-proposal-heading">
				<div className="new-proposal-intro">
					<span className="new-proposal-marker" aria-hidden="true">
						+
					</span>
					<div className="new-proposal-copy">
						<h2 id="new-proposal-heading">Start a new project</h2>
						<p>Shape an idea with the project agent, then submit it for review.</p>
					</div>
					{!newProposalOpen && (
						<button type="button" onClick={() => setNewProposalOpen(true)}>
							New proposal
						</button>
					)}
				</div>
				{newProposalOpen && (
					<form
						className="new-proposal-form"
						onSubmit={(event) => {
							event.preventDefault();
							void createProposal();
						}}
					>
						{organizationsLoading ? (
							<p className="new-proposal-owner-status" role="status">
								Checking project ownership…
							</p>
						) : organizationsFailed ? (
							<p className="new-proposal-owner-status error" role="alert">
								Organization choices could not be loaded. Refresh before creating a project.
							</p>
						) : eligibleOrganizations.length > 1 ? (
							<PortalSelect
								id="new-project-owner"
								className="new-proposal-owner"
								label="Project owner"
								value={owningOrganizationId}
								options={eligibleOrganizations.map((organization) => ({
									value: organization.id,
									label: organization.name,
									description: `${organization.name} will own this project.`,
								}))}
								onChange={setOwningOrganizationId}
								description={
									selectedOwningOrganization
										? `${selectedOwningOrganization.name} will own this project. Your membership role stays unchanged.`
										: "Choose the one organization that will own this project."
								}
								required
							/>
						) : soleEligibleOrganization ? (
							<div className="new-proposal-owner-summary">
								<span>Project owner</span>
								<strong>{soleEligibleOrganization.name}</strong>
								<small>
									{soleEligibleOrganization.name} will own this project. Your membership role stays unchanged.
								</small>
							</div>
						) : null}
						<label>
							Project name
							<input
								value={newTitle}
								onChange={(event) => setNewTitle(event.target.value)}
								placeholder="A short, recognizable name"
								maxLength={255}
								required
							/>
						</label>
						<label>
							What would you like to make?
							<textarea
								value={newBrief}
								onChange={(event) => setNewBrief(event.target.value)}
								placeholder="Describe the idea in your own words. You can shape the details with the project agent next."
								rows={5}
								required
							/>
						</label>
						<div className="form-actions">
							<button
								type="submit"
								disabled={creating || !newTitle.trim() || !newBrief.trim() || !ownershipReady}
							>
								{creating ? "Creating…" : "Create proposal"}
							</button>
							<button type="button" className="secondary" onClick={() => setNewProposalOpen(false)}>
								Cancel
							</button>
						</div>
					</form>
				)}
			</section>

			{actionError && <StatusBanner kind="error">{actionError}</StatusBanner>}
			{actionableProposals.length > 0 && (
				<section className="proposal-index" aria-labelledby="proposal-index-heading">
					<div className="section-heading-row">
						<div>
							<p className="eyebrow">BEFORE A PROJECT BEGINS</p>
							<h2 id="proposal-index-heading">Proposals to shape</h2>
						</div>
						<p>Continue the brief, talk through the direction, and submit it for Neal’s review.</p>
					</div>
					<div className="proposal-grid">
						{actionableProposals.map((proposal) => (
							<article className="proposal-card-shell" key={proposal.id}>
								<button className="proposal-card" type="button" onClick={() => onSelectProposal(proposal.id)}>
									<span className="project-state">{proposal.status}</span>
									<strong>{proposal.title}</strong>
									<span>{proposal.status === "submitted" ? "Waiting for review" : "Continue proposal"} →</span>
								</button>
								{proposal.kind === "requirementsUpdate" && proposal.status === "draft" && (
									<button
										type="button"
										className="quiet-action"
										onClick={() => void dismissRequirementsUpdate(proposal)}
									>
										Dismiss requirements update
									</button>
								)}
							</article>
						))}
					</div>
					{proposalPage.hasMore && (
						<button
							type="button"
							className="secondary"
							disabled={proposalPage.loadingMore}
							onClick={() => void proposalPage.loadMore()}
						>
							{proposalPage.loadingMore ? "Loading…" : "Load more proposals"}
						</button>
					)}
				</section>
			)}

			<section className="project-index" aria-labelledby="project-index-heading">
				<div className="section-heading-row project-index-heading">
					<div>
						<p className="eyebrow">APPROVED WORK</p>
						<h2 id="project-index-heading">Shared projects</h2>
					</div>
					{projects.length === 0 && actionableProposals.length > 0 ? (
						<p>Your first project will appear here after its proposal is approved.</p>
					) : (
						projects.length > 0 && (
							<PortalSelect
								id="project-organization-filter"
								className="organization-filter"
								label="Show projects"
								value={projectFilter}
								options={[
									{ value: "all", label: "All work" },
									...(projectGroups.some((group) => group.key === "direct")
										? [{ value: "direct", label: "Direct work" }]
										: []),
									...organizationGroups.map((group) => ({ value: group.key, label: group.name })),
								]}
								onChange={setProjectFilter}
								description="Filtering changes this view only. Your identity and access stay the same."
							/>
						)
					)}
				</div>
				<div className="project-groups">
					{visibleProjectGroups.map((group) => (
						<section className="project-group" aria-labelledby={`project-group-${group.key}`} key={group.key}>
							<div className="project-group-heading">
								<h3 id={`project-group-${group.key}`}>{group.name}</h3>
								<span>{group.organizationId ? "Organization-owned" : "Individually owned or granted"}</span>
							</div>
							<div className="project-grid">
								{group.projects.map((project) => (
									<article className="project-card-shell" key={project.id}>
										<button
											className="project-card"
											data-complete={project.isComplete}
											type="button"
											onClick={() => onSelectProject(project.id)}
										>
											<span
												className={`project-state${project.isComplete ? " project-state--complete" : ""}`}
											>
												{project.isComplete ? "Complete" : project.status}
											</span>
											<strong>{project.name}</strong>
											<span className="project-summary">{projectSummary(project)}</span>
										</button>
										<div className="project-access-info">
											<button
												type="button"
												aria-label={`Why you can access ${project.name}`}
												aria-expanded={accessInfoProjectId === project.id}
												aria-controls={`project-access-info-${project.id}`}
												title="Why you can access this project"
												onClick={() =>
													setAccessInfoProjectId((current) => (current === project.id ? null : project.id))
												}
											>
												<svg
													className="project-access-info-icon"
													viewBox="0 0 24 24"
													aria-hidden="true"
													focusable="false"
												>
													<path d="M12 10.75v6" />
													<circle className="project-access-info-icon-dot" cx="12" cy="7.5" r="0.8" />
												</svg>
											</button>
											{accessInfoProjectId === project.id && (
												<div className="project-access-info-panel" id={`project-access-info-${project.id}`}>
													<strong>Why you can access this</strong>
													<span>
														{project.accessSources?.map((source) => source.label).join(" · ") ||
															"Direct access"}
													</span>
												</div>
											)}
										</div>
									</article>
								))}
							</div>
						</section>
					))}
				</div>
				{projectPage.hasMore && (
					<button
						type="button"
						className="secondary"
						disabled={projectPage.loadingMore}
						onClick={() => void projectPage.loadMore()}
					>
						{projectPage.loadingMore ? "Loading…" : "Load more projects"}
					</button>
				)}
			</section>

			{projects.length === 0 && actionableProposals.length === 0 && !newProposalOpen && (
				<section className="empty-state empty-state-onboarding">
					<p className="eyebrow">START HERE</p>
					<h2>No shared work yet</h2>
					<p>Create a project proposal here, or claim an invitation someone has shared with you.</p>
					<div className="form-actions">
						<button type="button" onClick={onClaimInvitation}>
							Claim an invitation
						</button>
					</div>
				</section>
			)}
		</div>
	);
}
